import { useEffect, useState } from 'react';
import { ICharacter } from '@/models/characters';
import { SidebarLayout } from '@/components/layout/SidebarLayout';
import { Text } from '@/components/Text';
import { CollectionCharacterList } from '@/components/page-components/collection/CollectionCharacterList';
import { CharectersListSkeleton } from '@/components/page-components/home/CharectersListSkeleton';
import { useIsAuthenticated } from '@/hooks/useIsAuthenticated';
import { useCharactersStore } from '@/stores/characters';
import { useImageStore } from '@/stores/image';
import { Player } from '@lottiefiles/react-lottie-player';
import dotsAnimation from '@/assets/animations/dots.json';

export default function Collection() {
  const { characters } = useCharactersStore(s => ({
    characters: s.characters,
  }));

  const { characterImages, getUnlockedImagesSummary, loading, updateRequired } = useImageStore(s => ({
    characterImages: s.characterImages,
    getUnlockedImagesSummary: s.getUnlockedImagesSummary,
    loading: s.loading,
    updateRequired: s.updateRequired,
  }));

  const [charactersWithUnlockedImages, setCharactersWithUnlockedImages] = useState<ICharacter[]>([]);

  useEffect(() => {
    if (Object.keys(characterImages).length === 0 || updateRequired) {
      getUnlockedImagesSummary();
    }
  }, [characterImages, updateRequired, getUnlockedImagesSummary]);

  useEffect(() => {
    if (!Array.isArray(characters)) {
      setCharactersWithUnlockedImages([]);
      return;
    }
    
    const filteredCharacters = characters.filter(char => {
      return char && char.id && characterImages[char.id] && characterImages[char.id].image_count > 0;
    });
    setCharactersWithUnlockedImages(filteredCharacters);
  }, [characterImages, characters]);

  const isLoggedIn = useIsAuthenticated();

  return (
    <SidebarLayout>
      <div className="flex flex-col mb-16 md:mb-0">
        <Text className="mt-5 mb-5 text-h3 md:text-h3" variant="h3" textColor="white" element="h1">
          My Images
        </Text>
        {loading && (
          <div className="flex py-[10px]">
            <Player autoplay loop src={dotsAnimation} style={{ height: '40px', width: '80px' }} />
          </div>
        )}
        <CollectionCharacterList characters={charactersWithUnlockedImages} imageSummaries={characterImages} />
        {!charactersWithUnlockedImages?.length && loading && <CharectersListSkeleton />}
        {!isLoggedIn && !loading && (
          <div className="flex flex-col items-left justify-left h-full mt-4 ml-4">
            <Text className="text-white">Please log in to view your images.</Text>
          </div>          
        )}

        {isLoggedIn && !loading && !charactersWithUnlockedImages?.length && (
          <div className="flex flex-col items-left justify-left h-full mt-4 ml-4">
            <Text className="text-white">You haven't created or unlocked any images yet.</Text>
          </div>
        )}
      </div>
    </SidebarLayout>
  );
}
